import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Radar, Building2, TreePine, ArrowRight, Check } from 'lucide-react';
import { getImageUrl } from '../lib/api';

const PostaLogistikaSection = () => {
  const solutions = [
    {
      id: 1,
      slug: "izsekosana",
      icon: Radar,
      image: "logistics_tracking.webp",
      title: "Smalki sūtījumu izsekošanas risinājumi",
      description: "Kompakti izsekošanas risinājumi sūtījumu, maisu un loģistikas plūsmu pārraudzībai, īpaši piemēroti reģioniem un lauku teritorijām.",
      points: [
        "Ilgstoša autonomija",
        "Piemēroti loģistikas plūsmām",
        "Redzamība visā piegādes ķēdē"
      ]
    },
    {
      id: 2,
      slug: "daudzdzivoklu",
      icon: Building2,
      image: "apartment_locker.webp",
      title: "Autonomi pakuskapji daudzdzīvokļu ēkām",
      description: "Akumulatoru darbināti pakuskapji uzstādīšanai ēku ieejās (podezdos), kur nav pieejama pastāvīga elektrība vai infrastruktūra.",
      points: [
        "Bez sarežģītiem būvdarbiem",
        "Piemēroti vecām un jaunām ēkām",
        "Droša piekļuve iedzīvotājiem"
      ]
    },
    {
      id: 3,
      slug: "regioni",
      icon: TreePine,
      image: "rural_delivery_point.webp",
      title: "Piegādes risinājumi lauku teritorijām",
      description: "Autonomi pakuskapju punkti sūtījumu saņemšanai ciemos un mazapdzīvotās vietās, samazinot piegādes izmaksas un palielinot pieejamību.",
      points: [
        "Ātra ieviešana",
        "Piemēroti pašvaldībām",
        "Samazināts piegādes slogs"
      ]
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-28 bg-slate-50" id="pasta-logistika">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-slate-900 leading-tight mb-4 sm:mb-6">
            Pakuskapju un izsekošanas risinājumi pasta un loģistikas vajadzībām Latvijā
          </h2>
          <div className="w-12 sm:w-16 lg:w-20 h-1 bg-slate-800 mb-4 sm:mb-6" />
          <p className="text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed">
            Mēs piedāvājam specializētus risinājumus pasta un loģistikas operatoriem — no sūtījumu izsekošanas līdz autonomiem pakuskapjiem sarežģītās vidēs.
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
          {solutions.map((solution) => {
            const Icon = solution.icon;
            return (
              <div
                key={solution.id}
                className="bg-white rounded-xl lg:rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                {/* Image */}
                <div className="h-40 sm:h-48 lg:h-56 overflow-hidden">
                  <img
                    src={getImageUrl(solution.image)}
                    alt={solution.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>

                <div className="p-4 sm:p-6 lg:p-8 flex flex-col flex-grow">
                  {/* Icon */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-slate-800 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-5 -mt-10 sm:-mt-12 relative z-10 shadow-lg">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900 mb-2 sm:mb-3">
                    {solution.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 text-sm lg:text-base leading-relaxed mb-4 sm:mb-5">
                    {solution.description}
                  </p>

                  {/* Bullet Points */}
                  <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6 flex-grow">
                    {solution.points.map((point, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-slate-700">{point}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Learn More Link */}
                  <Link 
                    to={`/pasts-un-logistika#${solution.slug}`}
                    className="inline-flex items-center text-slate-800 font-medium text-sm sm:text-base hover:text-slate-600 group mt-auto"
                  >
                    Uzzināt vairāk
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/pasts-un-logistika">
            <Button
              variant="outline"
              size="lg"
              className="border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base group w-full sm:w-auto"
            >
              Skatīt risinājumus pasta un loģistikas operatoriem
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PostaLogistikaSection;
